import type { FlowplayerPlaylistQueue } from "./custom-flowplayer-playlist-ui-load";
import * as classes from "./custom-flowplayer-playlist-ui-styles.module.scss";
import { CAROUSEL_CLASS_NAME } from "../carousel/carousel-init";
import "../carousel/control-prev-next/prev-control-init";
import "../carousel/control-prev-next/next-control-init";
import { CAROUSEL_ITEM_CLASS_NAME } from "../carousel/slider/slider-elements-list";
import { initializeAndRegisterCarouselController } from "../carousel/carousel-handles";
import { CarouselControllerWillChangeSlideEvent } from "../carousel/controller/carousel-controller-will-change-slide-event";
import { CarouselSliderSlideChangeEvent } from "../carousel/slider/carousel-slider-slide-change-event";

export const createCustomFlowplayerPlaylistUi = (
  queue: FlowplayerPlaylistQueue,
  title: string,
  onUserSelectCallback: (
    index: number,
    updateActiveQueueElement: () => void
  ) => void
) => {
  const playlistElement = createPlaylistUI(title, queue, onUserSelectCallback);
  const playlistElementMobile = createPlaylistUI(
    "",
    queue,
    onUserSelectCallback,
    true
  );

  return {
    node: playlistElement,
    nodeMobile: playlistElementMobile,
    show: () => {
      playlistElement.removeAttribute("hidden");
    },
    hide: () => {
      playlistElement.setAttribute("hidden", "");
    },
    destroy: () => {
      playlistElement.remove();
      playlistElementMobile.remove();
    },
  } as const;
};

function createPlaylistUI(
  title: string,
  queue: FlowplayerPlaylistQueue,
  onUserSelectCallback: (
    index: number,
    updateActiveQueueElement: () => void
  ) => void,
  isMobile: boolean = false
) {
  const playlistElement = document.createElement("div");
  playlistElement.classList.add(
    isMobile ? classes.playListMobile : classes.playList
  );

  if (title !== "") {
    const titleElement = document.createElement("span");
    titleElement.classList.add(classes.headline);
    titleElement.append(title);

    playlistElement.append(titleElement);
  }

  const playlistCarouselElement = createPlaylistCarousel(
    queue,
    onUserSelectCallback,
    isMobile
  );
  playlistElement.append(playlistCarouselElement);

  return playlistElement;
}

const createPlaylistCarousel = (
  queue: FlowplayerPlaylistQueue,
  onUserSelectCallback: (
    index: number,
    updateActiveQueueElement: () => void
  ) => void,
  isMobile: boolean
) => {
  const memberListRootElement = document.createElement("ol");
  memberListRootElement.classList.add(classes.memberList);

  for (const [index, queueMember] of queue.members.entries()) {
    const queueMemberElement = document.createElement("li");
    queueMemberElement.classList.add(
      CAROUSEL_ITEM_CLASS_NAME,
      classes.queueItem,
      ...(queue.idx === index ? [classes.queueItemActive] : [])
    );
    queueMemberElement.title = queueMember.title;
    queueMemberElement.addEventListener("click", () =>
      onUserSelectCallback(index, () => {
        const queueElements = memberListRootElement.getElementsByClassName(
          classes.queueItem
        );
        for (const queryElement of queueElements) {
          queryElement.classList.remove(classes.queueItemActive);
        }

        queueMemberElement.classList.add(classes.queueItemActive);
      })
    );

    const thumbnailWrapperElement = document.createElement("div");
    thumbnailWrapperElement.classList.add(classes.thumbnailWrapper);

    const thumbnailElement = document.createElement("img");
    thumbnailElement.classList.add(classes.thumbnail);
    thumbnailElement.src = queueMember.poster;
    thumbnailElement.width = 36;
    thumbnailElement.height = 36;

    thumbnailWrapperElement.append(thumbnailElement);

    if (isMobile) {
      const thumbnailOverlayElement = document.createElement("div");
      thumbnailOverlayElement.classList.add(classes.thumbnailOverlay);

      thumbnailOverlayElement.append(createPauseIcon(), createPlayIcon());
      thumbnailWrapperElement.append(thumbnailOverlayElement);
    }

    const titleElement = document.createElement("div");
    titleElement.classList.add(classes.title);
    titleElement.append(queueMember.title);

    queueMemberElement.append(thumbnailWrapperElement, titleElement);
    memberListRootElement.append(queueMemberElement);
  }

  return createWrappingCarousel(memberListRootElement, isMobile);
};

const createWrappingCarousel = (
  listElement: HTMLOListElement,
  isMobile: boolean
) => {
  listElement.classList.add("carousel-inner");

  const viewWindowElement = document.createElement("div");
  viewWindowElement.classList.add("carousel-view-window", classes.viewThrough);
  viewWindowElement.append(listElement);

  const previousButton = createPrevButton();
  previousButton.classList.add(classes.carouselPrevNextButtonHidden);

  const nextButton = createNextButton();

  const wrappingElement = document.createElement("div");
  wrappingElement.classList.add(CAROUSEL_CLASS_NAME, "carousel__upper-row");

  wrappingElement.append(viewWindowElement, previousButton, nextButton);

  const updateVisibilityOfPrevNextButtons = () => {
    const currentScrollPosition = listElement.scrollLeft;

    const previousButtonWidth = Math.floor(
      previousButton.getBoundingClientRect().width
    );
    if (currentScrollPosition <= previousButtonWidth) {
      previousButton.classList.add(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.remove(classes.viewThroughNotAtStart);
    } else {
      previousButton.classList.remove(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.add(classes.viewThroughNotAtStart);
    }

    const nextButtonWidth = Math.floor(
      nextButton.getBoundingClientRect().width
    );
    const listFurthestViewThroughWindowStart =
      listElement.scrollWidth - viewWindowElement.scrollWidth;
    if (
      currentScrollPosition >=
      listFurthestViewThroughWindowStart - nextButtonWidth
    ) {
      nextButton.classList.add(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.remove(classes.viewThroughNotAtEnd);
    } else {
      nextButton.classList.remove(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.add(classes.viewThroughNotAtEnd);
    }
  };

  listElement.addEventListener("scroll", updateVisibilityOfPrevNextButtons, {
    passive: true,
  });

  const carouselController = initializeAndRegisterCarouselController(
    wrappingElement,
    {
      loop: false,
    }
  );

  // the mobile implementation uses native scroll-snap
  if (!isMobile) {
    /**
     * Counter the width of the prev button, if it is visible.
     */
    const setScrollOffsetAccoringToScroll = (
      event:
        | CarouselControllerWillChangeSlideEvent
        | CarouselSliderSlideChangeEvent
    ) => {
      // eslint-disable-next-line no-param-reassign
      event.scrollOffset =
        event.index > 0 ? previousButton.getBoundingClientRect().width : 0;
    };

    carouselController.addEventListener(
      CarouselControllerWillChangeSlideEvent.KEY,
      setScrollOffsetAccoringToScroll
    );
    carouselController.addEventListener(
      CarouselSliderSlideChangeEvent.KEY,
      setScrollOffsetAccoringToScroll
    );

    // We cannot use native scroll-snap here, since it aligns the last child
    // in scroll direction (inline-end when scrolling forward) with inline-end -
    // scroll-padding-inline-end. But we want to align the first child with
    // inline-start + scroll-padding-inline-start.
    //
    // We try to mimic scroll-snap behavior here and don't care about
    // safari not dispatching scrollend event, since this is only an
    // optional enhancement.
    listElement.addEventListener("scrollend", () => {
      carouselController.slideTo(carouselController.getCurrentIndex());
    });
  }

  new ResizeObserver(() => updateVisibilityOfPrevNextButtons()).observe(
    listElement
  );

  return wrappingElement;
};

const createPrevButton = () => {
  const button = createPrevNextButton(
    "Previous",
    "M-4608.225-11253.812l-16.7,16.7,16.7,16.7"
  );
  button.classList.add("carousel-control-prev");
  return button;
};

const createNextButton = () => {
  const button = createPrevNextButton(
    "Next",
    "M-4624.922-11253.812l16.7,16.7-16.7,16.7"
  );
  button.classList.add("carousel-control-next");
  return button;
};

const createPauseIcon = () => {
  const rect1 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  rect1.setAttribute("width", "4");
  rect1.setAttribute("height", "13");
  rect1.setAttribute("rx", "2");
  rect1.setAttribute("x", "0");

  const rect2 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  rect2.setAttribute("width", "4");
  rect2.setAttribute("height", "13");
  rect2.setAttribute("rx", "2");
  rect2.setAttribute("x", "6");

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.append(rect1, rect2);
  svg.classList.add(classes.pauseIcon);
  svg.setAttribute("height", "13");
  svg.setAttribute("width", "10");
  svg.setAttribute("viewBox", "0 0 10 13");

  return svg;
};

const createPlayIcon = () => {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute(
    "d",
    "M8.128,1.549a1,1,0,0,1,1.743,0l7.29,12.96A1,1,0,0,1,16.29,16H1.71a1,1,0,0,1-.872-1.49Z"
  );
  path.setAttribute("transform", "translate(16) rotate(90)");

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.append(path);
  svg.classList.add(classes.playIcon);
  svg.setAttribute("height", "18");
  svg.setAttribute("width", "16");
  svg.setAttribute("viewBox", "0 0 16 18");

  return svg;
};

const createPrevNextButton = (label: string, svgPathString: string) => {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.classList.add("icon__chevron");
  path.setAttribute("d", svgPathString);
  path.setAttribute("transform", "translate(4627.043 11254.873)");

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.append(path);
  svg.classList.add("icon--pop", classes.carouselPrevNextIcon);
  svg.setAttribute("height", "35.516");
  svg.setAttribute("width", "19.879");
  svg.setAttribute("viewBox", "0 0 19.879 35.516");

  const hiddenLabel = document.createElement("span");
  hiddenLabel.classList.add("sr-only");
  hiddenLabel.append(label);

  const button = document.createElement("button");
  button.append(svg, hiddenLabel);

  return button;
};
